import cn from 'classnames'
import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { get } from '../helpers/Api'
import { formatNumber, formatUSD } from '../helpers/FormatLinks'
import LoadingIndicator from '../loadingindicator'
import { useTheme } from 'next-themes'
const PriceHistory = React.memo((props) => {
    const { theme } = useTheme()
    const { t } = useTranslation('common')
    const assetId = props['assetId']
    const templateId = props['templateId']
    const [priceHistory, setPriceHistory] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    const embed = props['embed']

    const parseHistory = (res) => {
        if (!res['error']) {
            setPriceHistory(res)
        }
        setIsLoading(false)
    }

    const getHistory = async () => {
        if (assetId) {
            get('price-history/' + assetId, 'v3').then(parseHistory)
        } else {
            get('price-history-template/' + templateId, 'v3').then(parseHistory)
        }
    }

    useEffect(() => {
        if (!isLoading && (assetId || templateId)) {
            setIsLoading(true)
            getHistory()
        }
    }, [assetId, templateId])

    const getMaxSales = (sales) => {
        return Math.max(...sales) * 2.0
    }

    const chartOptions = priceHistory && {
        title: {
            text: '',
        },
        chart: {
            type: 'column',
            renderTo: 'Chart',
            backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        plotOptions: {
            areaspline: {
                marker: {
                    enabled: false,
                },
            },
            column: {
                maxPointWidth: 2,
            },
            series: {
                fillColor: {
                    linearGradient: [0, 100, 0, 300],
                    stops: [
                        [0, '#F6C30D'],
                        [1, '#1A395200'],
                    ],
                },
            },
        },
        yAxis: [
            {
                title: {
                    style: {
                        color: '#F6C30D',
                        'font-family': 'SFCompact',
                        'font-size': '12pt',
                    },
                    offset: 0,
                    rotation: 0,
                    y: -20,
                    align: 'high',
                    text: t('asset.average_price'),
                },
                labels: {
                    formatter: function () {
                        return formatUSD(this.value)
                    },
                },
                showFirstLabel: false,
            },
            {
                gridLineWidth: 0,
                opposite: true,
                max: getMaxSales(
                    priceHistory['priceHistory'].map((item) =>
                        item['sales'] ? item['sales'] : 0,
                    ),
                ),
                title: {
                    style: {
                        color: theme === 'light' ? '#000000' : '#FEFEFE',
                        'font-family': 'SFCompact',
                        'font-size': '12pt',
                    },
                    offset: 0,
                    rotation: 0,
                    y: -20,
                    align: 'high',
                    text: t('asset.amount_sold'),
                },
                labels: {
                    formatter: function () {
                        return formatNumber(this.value)
                    },
                },
                showFirstLabel: false,
            },
        ],
        xAxis: {
            type: 'datetime',
            categories: priceHistory['priceHistory'].map(
                (item) => item['date'],
            ),
            labels: {
                align: 'center',
                step:
                    Math.round(
                        Object.keys(priceHistory['priceHistory']).length / 3.0,
                    ) - 1,
                style: {
                    color: theme === 'light' ? '#000000' : '#FEFEFE',
                    'font-family': 'SFCompact',
                    'font-size': '10pt',
                },
            },
        },
        tooltip: {
            shared: true,
            backgroundColor:
                theme === 'light' ? '#FEFEFE' : 'rgba(0, 0, 0, 0.5)',
            style: {
                color: theme === 'light' ? '#000000' : '#FEFEFE',
                'font-family': 'SFCompact',
                'font-size': '11px',
            },
        },
        series: [
            {
                type: 'areaspline',
                borderWidth: 0,
                name: t('asset.average_price'),
                color: '#F6C30D',
                data: priceHistory['priceHistory'].map((item) => [
                    item['date'],
                    item['usdPrice'] ? item['usdPrice'] : 0,
                ]),
                tooltip: {
                    valuePrefix: '$',
                },
                yAxis: 0,
            },
            {
                borderWidth: 0,
                name: t('asset.amount_sold'),
                color: theme === 'light' ? '#000000' : '#FEFEFE',
                data: priceHistory['priceHistory'].map((item) => [
                    item['date'],
                    item['sales'] ? item['sales'] : 0,
                ]),
                yAxis: 1,
            },
        ],
        legend: {
            itemStyle: { color: theme === 'light' ? '#000000' : '#FEFEFE' },
        },
        credits: {
            enabled: false,
        },
    }

    return (
        <div className={cn('w-full mx-0 py-2 h-auto')}>
            {embed ? (
                ''
            ) : (
                <div className="text-left font-light dark:text-white text-black p-2 text-2xl lg:text-4xl">
                    {t('asset.price_history')}
                </div>
            )}
            {isLoading || !priceHistory ? (
                <LoadingIndicator />
            ) : (
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                />
            )}
        </div>
    )
})

export default PriceHistory
